import { queryOptions, useQuery } from "@tanstack/react-query";
import {
  featureFlagsGet,
  getFeatureFlagsGetQueryKey,
} from "@/services/api/feature-flags/feature-flags";
import { queryClient } from "@/services/queryClient";
import { useAuthStore } from "@/stores/authStore";
import type { FeatureFlagsResponse } from "@/types/api";

/** Every flag reads as off until the API answers, or if it can't (fail closed, matching the API). */
const FLAGS_OFF: FeatureFlagsResponse = {
  payments: false,
  learningPaths: false,
  ilt: false,
  seatOrders: false,
};

/**
 * PostHog flags are evaluated by the API per caller (no analytics consent or posthog-js needed), so the cache is
 * keyed by the signed-in user and re-fetched when they log in or out.
 */
export const featureFlagsQueryOptions = (userId: string | undefined) =>
  queryOptions({
    queryKey: [...getFeatureFlagsGetQueryKey(), userId ?? "anonymous"],
    queryFn: ({ signal }) => featureFlagsGet(signal),
    staleTime: 60_000,
  });

/** Resolves the current caller's flags for route guards (`beforeLoad`); failures resolve to all-off. */
export async function fetchFeatureFlags(): Promise<FeatureFlagsResponse> {
  return (await fetchFeatureFlagsIfAvailable()) ?? FLAGS_OFF;
}

/**
 * The current caller's flags, or null when the API couldn't answer, for a guard that must tell a flag that is off from
 * one it couldn't read (treat null as off, but don't act on it as if the flag were off).
 */
export async function fetchFeatureFlagsIfAvailable(): Promise<FeatureFlagsResponse | null> {
  try {
    return await queryClient.ensureQueryData(
      featureFlagsQueryOptions(useAuthStore.getState().user?.id),
    );
  } catch {
    return null;
  }
}

export function useFeatureFlags(): FeatureFlagsResponse {
  const userId = useAuthStore((state) => state.user?.id);
  const { data } = useQuery(featureFlagsQueryOptions(userId));
  return data ?? FLAGS_OFF;
}

/** Paid courses, Stripe checkout, discount codes, purchases, admin pricing and sales reporting. */
export const usePaymentsEnabled = () => useFeatureFlags().payments;

/** Learner-facing learning paths: the path pages and paths in the All courses table. */
export const useLearningPathsEnabled = () => useFeatureFlags().learningPaths;

/** Learner-facing instructor-led training: the Face-to-face training page, its course pages and their nav link. */
export const useIltEnabled = () => useFeatureFlags().ilt;

/**
 * Seat orders (LMS-140): buying several seats on a paid course and the purchaser's orders. Needs the payments flag
 * on as well, as the API does.
 */
export const useSeatOrdersEnabled = () => {
  const { payments, seatOrders } = useFeatureFlags();
  return Boolean(payments && seatOrders);
};
